import type { ColumnSort } from '@plato/ui/components/data-table';
import { useCallback, useMemo } from 'react';

import { SortOrder } from '../../../server/types/list-view-types.ts';

export type SortFilter<TField, TDirection extends 'ASC' | 'DESC' = SortOrder> = {
    field: TField;
    direction: TDirection;
};

export type UseGenericListViewSortingHook<TField> = [
    sorting: ColumnSort<TField> | undefined,
    onSortingChange: (sort: ColumnSort<TField> | undefined) => void,
];

export function useGenericListViewSorting<
    TField extends string,
    TDirection extends 'ASC' | 'DESC' = SortOrder,
>(
    sortingFilter: SortFilter<TField, TDirection> | null | undefined,
    setSortingFilter: (sortingFilter: SortFilter<TField, TDirection> | undefined) => void,
): UseGenericListViewSortingHook<TField> {
    const field = sortingFilter?.field;
    const direction = sortingFilter?.direction;

    const sorting = useMemo<ColumnSort<TField> | undefined>(
        () =>
            field === undefined || direction === undefined
                ? undefined
                : { by: field, direction: direction === SortOrder.DESC ? 'desc' : 'asc' },
        [field, direction],
    );

    const onSortingChange = useCallback(
        (sort: ColumnSort<TField> | undefined) => {
            setSortingFilter(
                sort && {
                    field: sort.by,
                    direction: (sort.direction === 'desc' ? SortOrder.DESC : SortOrder.ASC) as TDirection,
                },
            );
        },
        [setSortingFilter],
    );

    return [sorting, onSortingChange];
}
